<script>
import MottoIcon from '@components/motto-icon.vue'
import SurveyStarRating from '@components/survey/survey-star-rating.vue'
import LoaderIcon from '@components/loader-icon.vue'
import { isMobileMixin } from '@src/mixins/isMobileMixin'
import _ from 'lodash'
import { postSurvey } from '@src/api/survey'

export default {
  components: {
    LoaderIcon,
    MottoIcon,
    SurveyStarRating,
  },
  mixins: [isMobileMixin],
  props: {
    surveyQuestion: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      addMargin: false,
      yVisibleOffset: 100,
      isOpened: false,
      submitting: false,
      submitted: false,
      hidden: false,
      surveyAnswers: {},
    }
  },
  computed: {
    noQuestionsAnswered() {
      const questions = this.surveyQuestion.questions.map((x) => ({ id: x.id, type: x.type }))
      for (const { id, type } of questions) {
        const surveyAnswer = this.surveyAnswers[id] || ''
        if (type === 'default' && surveyAnswer.length > 1) {
          return false
        }

        if (type !== 'default' && this.surveyAnswers[id]) {
          return false
        }
      }

      return true
    },

    shouldAddMargin() {
      return !this.isOpened && this.addMargin
    },
  },
  created() {
    const seenSurveys = this.$store.getters['user/seenSurveys']

    if (!seenSurveys.includes(this.surveyQuestion.id)) {
      this.isOpened = true
      this.$store.commit('user/SET_SEEN_SURVEYS', this.surveyQuestion.id)
    }
  },
  mounted() {
    window.addEventListener('scroll', this.handleScroll)
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.handleScroll)
  },
  methods: {
    handleScroll() {
      if (!this.isMobile) {
        this.addMargin = false
        return
      }

      this.addMargin = window.scrollY > this.yVisibleOffset
    },

    updateStarRating(questionId, starRating) {
      const tempSurveyAnswers = _.cloneDeep(this.surveyAnswers)
      tempSurveyAnswers[questionId] = starRating
      this.surveyAnswers = tempSurveyAnswers
    },

    startSurvey() {
      this.isOpened = true
    },

    dismissSurvey() {
      this.isOpened = false
    },

    getAnsweredQuestionsForSurvey() {
      return {
        questions: this.surveyQuestion.questions.reduce((acc, curr) => {
          const response = { id: curr.id }
          const answer = this.surveyAnswers[curr.id]
          if (curr.type === 'default') {
            response.response = answer
          }

          if (curr.type === 'rating') {
            response.rating = answer
          }

          if (curr.type === 'multiple') {
            response.surveyResponseOptionId = parseInt(answer)
          }

          return [...acc, response]
        }, []),
      }
    },

    async submitSurvey() {
      try {
        this.submitting = true
        await postSurvey({ surveyResponses: this.getAnsweredQuestionsForSurvey() })
        this.submitted = true
        setTimeout(this.selfDestruct, 2000)
      } catch (err) {
        this.$root.$bvToast.toast('There was an error trying to save your feedback. Please try again later', {
          toaster: 'b-toaster-bottom-center',
          toastClass: 'toast-error',
        })
      }
    },

    selfDestruct() {
      this.hidden = true
    },
  },
}
</script>

<template
  ><div v-if="!hidden" class="d-contents">
    <div
      v-if="!isOpened"
      :class="[$style.surveyRiderContainer, shouldAddMargin ? $style.extraMarginTop : '']"
      @click="startSurvey">
      <MottoIcon :class="$style.squareIcon" name="SquareStar" icon-color="orange"></MottoIcon>
    </div>

    <div v-if="isOpened" :class="[$style.surveyContent, shouldAddMargin ? $style.extraMarginTop : '']">
      <div :class="$style.title"
        ><p>{{ surveyQuestion.title }}</p></div
      >
      <template v-if="submitted"
        ><div class="d-flex h-100 w-100 mt-3 align-items-center justify-content-center"
          ><p class="p1">Thank you for your feedback!</p></div
        ></template
      >
      <template v-else
        ><div v-for="question in surveyQuestion.questions" :key="question.id">
          <div v-if="question.type === 'default'" :class="$style.surveyDefault">
            <p class="p1 font-weight-bold">{{ question.body }}</p>
            <BaseInputText v-model="surveyAnswers[question.id]" textarea />
          </div>
          <div v-if="question.type === 'rating'" :class="$style.surveyStar">
            <p class="p1 font-weight-bold">{{ question.body }}</p>
            <SurveyStarRating
              :least-star-description="question.lowestRatingDescription"
              :most-star-description="question.highestRatingDescription"
              @star-rating="
                (star) => {
                  updateStarRating(question.id, star)
                }
              " />
          </div>
          <div v-if="question.type === 'multiple'" :class="$style.surveyMultiple">
            <p class="p1 font-weight-bold">{{ question.body }}</p>
            <BaseRadioButton
              v-for="response in question.responseOptions"
              :key="`${response.id}${response.surveyQuestionId}`"
              v-model="surveyAnswers[question.id]"
              theme="simple"
              :label="response.option"
              :name="`${response.surveyQuestionId}`"
              :value="response.id" />
          </div>
        </div>
        <div :class="$style.buttons">
          <BaseButton theme="orange" @click="dismissSurvey">Dismiss</BaseButton>
          <BaseButton theme="orange" :disabled="noQuestionsAnswered || submitting" @click="submitSurvey"
            ><template v-if="submitting"><LoaderIcon /></template>
            <template v-else>Submit</template>
          </BaseButton>
        </div>
      </template>
    </div>
  </div>
</template>

<style lang="scss" module>
@import '@design';

.squareIcon {
  position: absolute;
  top: 50%;
  left: 50%;
  z-index: 20;
  cursor: pointer;
  transform: translateX(-50%) translateY(-50%);

  svg {
    display: inline;
    width: 64px;
    height: 64px;
  }
}
.surveyRiderContainer {
  @media (max-width: $lg-breakpoint) {
    bottom: 100px;
  }

  position: fixed;
  right: 22px;
  bottom: 20px;
  z-index: 999;
  width: 60px;
  height: 60px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.surveyContent {
  @media (max-width: $lg-breakpoint) {
    right: 0;
    bottom: 100px;
    width: 100%;
    max-height: 80vh;
  }

  position: fixed;
  right: 22px;
  bottom: 20px;
  z-index: 999;
  display: flex;
  flex-direction: column;
  width: 370px;
  min-height: 140px;
  max-height: 90vh;
  overflow: auto;
  background: $white;
  border-radius: 8px;
  box-shadow: 0 0 6px 3px rgba(196, 196, 196, 0.3);
  transition: all 0.3s ease;
}

.extraMarginTop {
  @media (max-width: $lg-breakpoint) {
    bottom: 170px;
  }

  bottom: 100px;
}

.title {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 40px;
  margin-bottom: 24px;
  background: $secondary-orange;

  p {
    font-size: 14px;
    font-weight: bold;
    color: white;
    text-align: center;
    letter-spacing: 0.3px;
  }
}

.surveyDefault {
  padding: 0 16px 12px;

  p {
    padding: 0 0 12px 0;
  }

  div {
    padding-left: 16px;
  }

  textarea {
    color: #000;
    border-color: #000;
  }
}

.surveyMultiple {
  padding: 0 16px 12px;

  > p {
    margin-bottom: 4px;
  }

  div {
    padding: 4px 16px;
  }
}

.surveyStar {
  padding: 0 16px 12px;

  p {
    padding: 0 0 8px 0;
  }
}

.buttons {
  position: sticky;
  bottom: 0;
  display: flex;
  justify-content: center;
  width: 100%;
  padding-top: 12px;
  padding-bottom: 16px;
  margin-top: auto;
  background: $white;
  gap: 16px;
}
</style>
